import { useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Typography,
  Box,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Link,
} from "@mui/material";
import Spinner from "features/spinner/Spinner";
import { TimePeriod, TimePeriodValues } from "models/UserStatistics";
import OutlinedCard from "components/OutlinedCard";
import Section from "components/Section";
import SectionHeader from "components/SectionHeader";
import SearchIcon from "@mui/icons-material/Search";
import DataTable, { ColumnOptions } from "components/DataTable";
import { CompletedChecklist } from "models/UserStatistics";
import { parseAsBoolean, parseAsStringEnum, useQueryState } from "nuqs";
import { useGetCompletedChecklistsQuery } from "app/api/users";
import { useUserContext } from "context/UserContext";

const columns: ColumnOptions<CompletedChecklist> = [
  {
    label: "Checklista",
    key: "roomName",
    renderCell: (row) => (
      <Link
        component={RouterLink}
        to={`/orders/view/${row.orderNumber}/checklist/${row.checklistId}`}
      >
        {row.roomName}
      </Link>
    ),
  },
  {
    label: "Tillhör order",
    key: "orderNumber",
    renderCell: (row) => (
      <Link component={RouterLink} to={`/orders/view/${row.orderNumber}`}>
        {row.orderNumber}
      </Link>
    ),
  },
  { label: "Skapad", key: "created", align: "right" },
  { label: "Slutförd", key: "completedAt", align: "right" },
];

const CompletedInstallations: React.FC = () => {
  const user = useUserContext();
  const [search, setSearch] = useQueryState("search", { defaultValue: "" });
  const [timePeriod, setTimePeriod] = useQueryState(
    "timePeriod",
    parseAsStringEnum<TimePeriod>([
      "FROM_START",
      "PAST_WEEK",
      "PAST_MONTH",
      "PAST_YEAR",
    ]).withDefault("FROM_START")
  );
  const [onlyApproved, setOnlyApproved] = useQueryState(
    "onlyApproved",
    parseAsBoolean.withDefault(true)
  );

  const { data: completedChecklists, isLoading: isChecklistsLoading } =
    useGetCompletedChecklistsQuery({
      userId: user.id,
      timePeriod,
      onlyIncludeApprovedChecklists: onlyApproved,
    });

  const filteredChecklists = useMemo(() => {
    return completedChecklists?.filter(
      (checklist) =>
        checklist.roomName.toLowerCase().includes(search.toLowerCase()) ||
        checklist.orderNumber.toLowerCase().includes(search.toLowerCase())
    );
  }, [completedChecklists, search]);

  if (user.state === "PENDING") return null;

  return (
    <Section mt={1}>
      <SectionHeader title="Installationer">
        {!!completedChecklists?.length && (
          <Typography fontWeight="bold">{`${completedChecklists.length} st`}</Typography>
        )}
      </SectionHeader>

      <OutlinedCard>
        {isChecklistsLoading ? (
          <Box sx={{ display: "grid", placeItems: "center", gap: 2, my: 5 }}>
            <Spinner size="3rem" color="#5f5f5f" />
            <Typography>Hämtar checklistor...</Typography>
          </Box>
        ) : (
          <>
            <Box display="flex" alignItems="center" gap={1}>
              <TextField
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ width: "100%", maxWidth: "30ch" }}
                variant="filled"
                label="Sök checklista, ordernr"
                type="search"
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <FormControl variant="filled" sx={{ minWidth: 200, flexGrow: 0 }}>
                <InputLabel>Period</InputLabel>
                <Select
                  value={timePeriod}
                  onChange={(e) => setTimePeriod(e.target.value as TimePeriod)}
                >
                  <MenuItem value="FROM_START">{TimePeriodValues.FROM_START}</MenuItem>
                  <MenuItem value="PAST_WEEK">{TimePeriodValues.PAST_WEEK}</MenuItem>
                  <MenuItem value="PAST_MONTH">{TimePeriodValues.PAST_MONTH}</MenuItem>
                  <MenuItem value="PAST_YEAR">{TimePeriodValues.PAST_YEAR}</MenuItem>
                </Select>
              </FormControl>

              <FormControlLabel
                label="Endast godkända"
                control={
                  <Switch checked={onlyApproved} onChange={() => setOnlyApproved(!onlyApproved)} />
                }
              />
            </Box>

            <DataTable
              columns={columns}
              data={filteredChecklists!}
              getKey={(row) => row.checklistId}
              striped
            />
          </>
        )}
      </OutlinedCard>
    </Section>
  );
};

export default CompletedInstallations;
